import React from 'react';
import { Link } from 'react-router-dom';
import { FileTextIcon, CheckCircle2Icon, TriangleAlertIcon, EyeIcon, ArrowUpIcon, ArrowRightIcon, ArrowUpRightIcon, ChevronDownIcon, UserRoundIcon, BarChart3Icon, SparklesIcon, MapPinIcon, BellIcon, CalendarIcon, UsersIcon, CreditCardIcon, ShieldCheckIcon, SignalIcon } from 'lucide-react';
import { kpis, volumeBars, volumeAxis, servicePerformance, sectorDetail, recommendation, digitalReach, type Status } from '../../../data/requestsIntelligence';
import { trackEvent as track } from '../../../utils/analytics';
type P = {
  isFR: boolean;
};
const BAR: Record<Status, string> = {
  red: '#E85B5B',
  amber: '#F0B24A',
  green: '#2FB25A'
};
const TEXT: Record<Status, string> = {
  red: 'text-[#D64545]',
  amber: 'text-[#D98A12]',
  green: 'text-[#22A55B]'
};
const panel = 'rounded-xl border border-[#ECECF3] bg-white';
function PanelTitle({
  icon: Icon,
  title
}: {
  icon: typeof FileTextIcon;
  title: string;
}) {
  return <span className="flex items-center gap-2.5" data-id="element-5789" data-mp="dsd7b37sxvbf">
      <span className="w-8 h-8 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-5790" data-mp="dqjz3jhjj31b">
        <Icon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.8} data-id="element-5791" data-mp="4ryjnx7d844r" />
      </span>
      <span className="font-sans text-[13.5px] font-semibold text-[#121212]" data-id="element-5792" data-mp="4u184csvdp7d">{title}</span>
    </span>;
}
function Bar({
  value,
  status,
  className = ''
}: {
  value: number;
  status: Status;
  className?: string;
}) {
  return <span className={`block h-[6px] rounded-full bg-[#F0F1F4] overflow-hidden ${className}`} data-id="element-5793" data-mp="3vxzoho0qm1hy">
      <span className="block h-full rounded-full" style={{
      width: `${value}%`,
      backgroundColor: BAR[status]
    }} data-id="element-5794" data-mp="3h9d6tfnn0xnk" />
    </span>;
}
export function KpiRow({
  isFR
}: P) {
  const cfg = {
    indigo: {
      icon: FileTextIcon,
      bg: 'bg-[#F1F0FD]',
      ic: 'text-[#4F46E5]',
      note: 'text-[#22A55B]',
      arrow: true
    },
    green: {
      icon: CheckCircle2Icon,
      bg: 'bg-[#EAF7EF]',
      ic: 'text-[#22A55B]',
      note: 'text-[#22A55B]',
      arrow: true
    },
    red: {
      icon: TriangleAlertIcon,
      bg: 'bg-[#FDECEC]',
      ic: 'text-[#D64545]',
      note: 'text-[#D64545]',
      arrow: false
    },
    amber: {
      icon: EyeIcon,
      bg: 'bg-[#F1F0FD]',
      ic: 'text-[#4F46E5]',
      note: 'text-[#D98A12]',
      arrow: true
    }
  } as const;
  return <ul className="grid grid-cols-2 xl:grid-cols-4 gap-3" data-id="element-5795" data-mp="191zzrxgx0iix">
      {kpis.map(k => {
      const c = cfg[k.tone];
      const Icon = c.icon;
      return <li key={k.key} className={`${panel} p-4 flex items-start gap-3.5`} data-id="element-5796" data-mp="21pcmq8qzmscr">
            <span className={`w-11 h-11 rounded-full ${c.bg} flex items-center justify-center shrink-0`} aria-hidden="true" data-id="element-5797" data-mp="1wqt169gb3ikx">
              <Icon className={`w-5 h-5 ${c.ic}`} strokeWidth={1.7} data-id="element-5798" data-mp="1soeh5m0wics2" />
            </span>
            <div className="min-w-0" data-id="element-5799" data-mp="1nrfrbfo9srzo">
              <p className="font-sans text-[22px] md:text-[24px] font-semibold text-[#121212] leading-none tabular-nums" data-id="element-5800" data-mp="1nqcvctc3bzcw">
                {isFR ? k.value.fr : k.value.en}
              </p>
              <p className="font-sans text-[12px] text-[#4A5160] mt-1.5" data-id="element-5801" data-mp="c9wi96o0dut7">{isFR ? k.label.fr : k.label.en}</p>
              <p className={`font-sans text-[11px] mt-1 flex items-center gap-1 ${c.note}`} data-id="element-5802" data-mp="2o0noz235cjsc">
                {c.arrow && <ArrowUpIcon className="w-3 h-3 shrink-0" strokeWidth={2} aria-hidden="true" data-id="element-5803" data-mp="2svtesbzo6ilp" />}
                {isFR ? k.note.fr : k.note.en}
              </p>
            </div>
          </li>;
    })}
    </ul>;
}
export function VolumeChart({
  isFR
}: P) {
  const max = 2000;
  const ticks = isFR ? ['2 000', '1 500', '1 000', '500', '0'] : ['2,000', '1,500', '1,000', '500', '0'];
  return <div className={`${panel} p-4 h-full flex flex-col`} data-id="element-5804" data-mp="3bqi2zmz7cee6">
      <div className="flex items-center justify-between gap-2" data-id="element-5805" data-mp="3gojnmcca0lrr">
        <PanelTitle icon={BarChart3Icon} title={isFR ? 'Volume des demandes' : 'Request volume'} data-id="element-5806" data-mp="3kotsfloc1651" />
        <span className="inline-flex items-center gap-1 rounded-md border border-[#E6E7EE] px-2 py-1 font-sans text-[10px] text-[#4A5160] whitespace-nowrap" data-id="element-5807" data-mp="1m9qqu9znpoy8">
          {isFR ? '30 derniers jours' : 'Last 30 days'}
          <ChevronDownIcon className="w-3 h-3" aria-hidden="true" data-id="element-5808" data-mp="v0cq5lgyr436" />
        </span>
      </div>
      <div className="mt-4 flex-1 grid grid-cols-[34px_minmax(0,1fr)] gap-2 min-h-[150px]" data-id="element-5809" data-mp="qztybrf2ly0z">
        <div className="flex flex-col justify-between pb-5 font-sans text-[10px] text-[#8A90A0] text-right tabular-nums" data-id="element-5810" data-mp="14ytmyz2l2cx5">
          {ticks.map(tk => <span key={tk} data-id="element-5811" data-mp="100s3id18wbl5">{tk}</span>)}
        </div>
        <div className="flex flex-col" data-id="element-5812" data-mp="c9mmvvnhdf9s">
          <div className="relative flex-1 flex items-end gap-[3px] border-b border-[#ECEDF2]" data-id="element-5813" data-mp="c8bmm5t8upnf">
            {[25, 50, 75].map(p => <span key={p} className="absolute left-0 right-0 border-t border-dashed border-[#F0F1F5]" style={{
            bottom: `${p}%`
          }} aria-hidden="true" data-id="element-5814" data-mp="lzxx1zxrckp4" />)}
            {volumeBars.map((v, i) => <span key={i} className={`relative flex-1 rounded-t-[3px] ${i % 3 === 2 ? 'bg-[#B9B4F4]' : 'bg-[#D9D6FA]'}`} style={{
            height: `${v / max * 100}%`
          }} data-id="element-5815" data-mp="3porhtcuphk59" data-mpi={i} />)}
          </div>
          <div className="flex justify-between pt-1.5 font-sans text-[10px] text-[#8A90A0]" data-id="element-5816" data-mp="2062nhtcbc41b">
            {volumeAxis.map(a => <span key={a.en} data-id="element-5817" data-mp="1r80r5r3ixzkd">{isFR ? a.fr : a.en}</span>)}
          </div>
        </div>
      </div>
    </div>;
}
export function PerformanceTable({
  isFR
}: P) {
  return <div className={`${panel} p-4 h-full`} data-id="element-5818" data-mp="2iyn9cqtlh8vm">
      <div className="flex items-center justify-between gap-2" data-id="element-5819" data-mp="2izyeatys0mob">
        <PanelTitle icon={UserRoundIcon} title={isFR ? 'Performance par service' : 'Performance by service'} data-id="element-5820" data-mp="2ny9pp94pz58n" />
        <span className="inline-flex items-center gap-1 font-sans text-[11px] text-[#4F46E5] whitespace-nowrap" data-id="element-5821" data-mp="29yrwmhke55th">
          {isFR ? 'Voir tous les services' : 'View all services'}
          <ArrowRightIcon className="w-3 h-3" aria-hidden="true" data-id="element-5822" data-mp="yj38vnzbc2rm" />
        </span>
      </div>
      <div className="mt-3 overflow-x-auto" data-id="element-5823" data-mp="13hwobly5u3uf">
        <table className="w-full min-w-[420px] font-sans text-[12px]" data-id="element-5824" data-mp="13io901q7yh5x">
          <thead data-id="element-5825" data-mp="357loytp1w1rt">
            <tr className="bg-[#F8F8FB] text-[#6B7180] text-[11px]" data-id="element-5826" data-mp="3560vl7eh2ovj">
              <th className="text-left font-medium px-3 py-2 rounded-l-md" data-id="element-5827" data-mp="308960k1aveui">{isFR ? 'Service' : 'Service'}</th>
              <th className="text-left font-medium px-3 py-2" data-id="element-5828" data-mp="2vgr5ty73pial">{isFR ? 'Demandes' : 'Requests'}</th>
              <th className="text-left font-medium px-3 py-2" data-id="element-5829" data-mp="2rg738f3rdmh2">{isFR ? 'Performance' : 'Performance'}</th>
              <th className="text-right font-medium px-3 py-2 rounded-r-md" data-id="element-5830" data-mp="2mguj4249uti6">{isFR ? 'Écart par rapport à la cible' : 'Gap vs target'}</th>
            </tr>
          </thead>
          <tbody data-id="element-5831" data-mp="2cieuq6wmpycd">
            {servicePerformance.map(row => <tr key={row.service.en} className="border-b border-[#F2F2F6] last:border-0" data-id="element-5832" data-mp="2cioiroy45vff">
                <td className="px-3 py-2.5 text-[#121212]" data-id="element-5833" data-mp="28ie65tftg6rl">{isFR ? row.service.fr : row.service.en}</td>
                <td className="px-3 py-2.5 text-[#4A5160] tabular-nums" data-id="element-5834" data-mp="aq8paymapx6m">{isFR ? row.requests.fr : row.requests.en}</td>
                <td className="px-3 py-2.5" data-id="element-5835" data-mp="6nlvwt9z847b">
                  <span className="flex items-center gap-3" data-id="element-5836" data-mp="1om0bljc4dfkb">
                    <Bar value={row.perf} status={row.status} className="flex-1 min-w-[80px]" data-id="element-5837" data-mp="1tm65z79rxg63" />
                    <span className="w-9 text-right text-[#121212] tabular-nums" data-id="element-5838" data-mp="1ycuympx6wj6t">
                      {row.perf}
                      {isFR ? ' %' : '%'}
                    </span>
                  </span>
                </td>
                <td className={`px-3 py-2.5 text-right font-medium tabular-nums ${TEXT[row.status]}`} data-id="element-5839" data-mp="15oz49rv3f3d7">
                  {isFR ? row.gap.fr : row.gap.en}
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
export function SectorPanel({
  isFR
}: P) {
  const d = sectorDetail;
  return <div className={`${panel} p-5 h-full`} data-id="element-5840" data-mp="1jp9x30fyqs10">
      <div className="flex items-center gap-2.5" data-id="element-5841" data-mp="2q4k8w0wbk0zk">
        <p className="font-sans text-[15px] font-semibold text-[#121212]" data-id="element-5842" data-mp="2q3sqxsvna1rr">{isFR ? d.name.fr : d.name.en}</p>
        <span className="rounded-full border border-[#F3B8B8] bg-[#FDECEC] px-2 py-0.5 font-sans text-[10px] font-medium text-[#D64545]" data-id="element-5843" data-mp="2v58bljtwu69m">
          {isFR ? d.status.fr : d.status.en}
        </span>
      </div>
      <div className="mt-3 flex items-end justify-between gap-2" data-id="element-5844" data-mp="21pnoyxpuhdi8">
        <p className="font-sans text-[13px] text-[#2A2F3C]" data-id="element-5845" data-mp="2fkgcojfw1zzw">{isFR ? d.requests.fr : d.requests.en}</p>
        <p className="font-sans text-[10.5px] text-[#8A90A0]" data-id="element-5846" data-mp="2aqly1uf54b7o">
          {isFR ? d.slaLabel.fr : d.slaLabel.en}{' '}
          <span className="text-[16px] font-semibold text-[#D64545] ml-1" data-id="element-5847" data-mp="2pj5xajz65yju">{isFR ? d.sla.fr : d.sla.en}</span>
        </p>
      </div>
      <p className="font-sans text-[11.5px] font-medium text-[#121212] mt-6" data-id="element-5848" data-mp="2yhhiu7l6i65f">{isFR ? d.affectedTitle.fr : d.affectedTitle.en}</p>
      <ul className="mt-4 space-y-5" data-id="element-5849" data-mp="2uh7h574f61wd">
        {d.affected.map(a => <li key={a.label.en} data-id="element-5850" data-mp="1436us1cerg0p">
            <span className="flex items-center justify-between font-sans text-[12px]" data-id="element-5851" data-mp="144hzq4hlatte">
              <span className="text-[#2A2F3C]" data-id="element-5852" data-mp="1036ioncs7gwt">{isFR ? a.label.fr : a.label.en}</span>
              <span className={`font-medium tabular-nums ${TEXT[a.status]}`} data-id="element-5853" data-mp="pxy62ftyxw15">
                {a.perf}
                {isFR ? ' %' : '%'}
              </span>
            </span>
            <Bar value={a.perf} status={a.status} className="mt-2" data-id="element-5854" data-mp="g8epxhxn4g9z" />
          </li>)}
      </ul>
    </div>;
}
const ACTION_ICONS = {
  target: MapPinIcon,
  notify: BellIcon,
  schedule: CalendarIcon
} as const;
export function RecommendationPanel({
  isFR
}: P) {
  const r = recommendation;
  return <div className="rounded-xl border border-[#DAD7F7] bg-white p-5 h-full flex flex-col shadow-[0_14px_34px_-24px_rgba(79,70,229,0.55)]" data-id="element-5855" data-mp="b922auyy356c">
      <div className="flex items-center justify-between gap-2" data-id="element-5856" data-mp="3tg87xtnowspq">
        <span className="flex items-center gap-2 font-sans text-[13px] font-semibold text-[#4F46E5]" data-id="element-5857" data-mp="283u00hh4bhy">
          <SparklesIcon className="w-4 h-4" strokeWidth={1.8} aria-hidden="true" data-id="element-5858" data-mp="1rn0m01dkncuv" />
          {isFR ? r.heading.fr : r.heading.en}
        </span>
        <span className="rounded-full border border-[#F3B8B8] px-2 py-0.5 font-sans text-[10px] font-medium text-[#D64545] whitespace-nowrap" data-id="element-5859" data-mp="1wlvczqlp07q2">
          {isFR ? r.pill.fr : r.pill.en}
        </span>
      </div>
      <p className="font-sans text-[15px] font-semibold text-[#121212] leading-snug mt-3" data-id="element-5860" data-mp="1i0982nod9s5a">{isFR ? r.title.fr : r.title.en}</p>
      <p className="font-sans text-[12px] text-[#5B6478] leading-relaxed mt-2" data-id="element-5861" data-mp="188d08kwhannv">{isFR ? r.body.fr : r.body.en}</p>
      <ul className="mt-3 rounded-lg bg-[#F4F3FD] px-3 py-2.5 space-y-2" data-id="element-5862" data-mp="1vxfpadtfzise">
        {r.actions.map(a => {
        const Icon = ACTION_ICONS[a.key as keyof typeof ACTION_ICONS];
        return <li key={a.key} className="flex items-center gap-2.5 font-sans text-[11.5px] text-[#3A3F7A]" data-id="element-5863" data-mp="1mxsnhqwswayt">
              <Icon className="w-3.5 h-3.5 text-[#4F46E5] shrink-0" strokeWidth={1.8} aria-hidden="true" data-id="element-5864" data-mp="1rvkpxev2nfh9" />
              {isFR ? a.label.fr : a.label.en}
            </li>;
      })}
      </ul>
      <div className="flex-1" aria-hidden="true" data-id="element-5865" data-mp="ghgus5cqzlam" />
      <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
      location: 'Requests Intelligence Recommendation'
    })} className="mt-4 flex items-center justify-center gap-2 rounded-md bg-[#4F46E5] px-4 py-2.5 font-sans text-[13px] font-medium text-white transition-colors duration-150 hover:bg-[#4338CA]" data-id="element-5866" data-mp="gf45f74nu8nj">
        {isFR ? r.cta.fr : r.cta.en}
        <ArrowUpRightIcon className="w-3.5 h-3.5" strokeWidth={2} aria-hidden="true" data-id="element-5867" data-mp="d02lxeeclvya" />
      </Link>
    </div>;
}
const REACH_ICONS = {
  ids: UsersIcon,
  cards: CreditCardIcon,
  reach: ShieldCheckIcon,
  open: EyeIcon
} as const;
export function DigitalReachBand({
  isFR
}: P) {
  const d = digitalReach;
  return <div className={`${panel} px-5 py-4 grid grid-cols-2 lg:grid-cols-[minmax(0,1.5fr)_repeat(4,minmax(0,1fr))] gap-y-5 items-center`} data-id="element-5868" data-mp="3v6z34txp5ke3">
      <div className="col-span-2 lg:col-span-1 flex items-center gap-3 lg:pr-5" data-id="element-5869" data-mp="32o1vy20s0icl">
        <span className="w-10 h-10 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-5870" data-mp="2xpi9m0i9t4jp">
          <SignalIcon className="w-[18px] h-[18px] text-[#4F46E5]" strokeWidth={1.7} data-id="element-5871" data-mp="11mvknz8f1yq1" />
        </span>
        <span data-id="element-5872" data-mp="16kneiysmfuwv">
          <span className="block font-sans text-[13.5px] font-semibold text-[#4F46E5]" data-id="element-5873" data-mp="16kf3lcg26zob">{isFR ? d.title.fr : d.title.en}</span>
          <span className="block font-sans text-[11.5px] text-[#5B6478]" data-id="element-5874" data-mp="1bcyo6td5yv2m">{isFR ? d.sub.fr : d.sub.en}</span>
        </span>
      </div>
      {d.metrics.map(m => {
      const Icon = REACH_ICONS[m.key as keyof typeof REACH_ICONS];
      return <div key={m.key} className="flex items-center gap-3 lg:border-l lg:border-[#ECECF3] lg:pl-5" data-id="element-5875" data-mp="nm2t9ndevjr5">
            <span className="w-10 h-10 rounded-full bg-[#F1F0FD] flex items-center justify-center shrink-0" aria-hidden="true" data-id="element-5876" data-mp="rnwmxbp6vnht">
              <Icon className="w-[18px] h-[18px] text-[#4F46E5]" strokeWidth={1.7} data-id="element-5877" data-mp="wou3jjujw9h7" />
            </span>
            <span className="min-w-0" data-id="element-5878" data-mp="232ti73byv8xm">
              <span className="block font-sans text-[17px] font-semibold text-[#121212] leading-tight tabular-nums" data-id="element-5879" data-mp="282fv1bnvun5w">
                {isFR ? m.value.fr : m.value.en}
              </span>
              <span className="block font-sans text-[11px] text-[#5B6478] leading-snug" data-id="element-5880" data-mp="2i879zrjv4nuh">{isFR ? m.label.fr : m.label.en}</span>
            </span>
          </div>;
    })}
    </div>;
}